import React from 'react';
import SelectMenu from '../shared/SelectMenu';

/**
 * ParkFilter — the in-page park selector for multi-park operator logins
 * (Epic 9 · S9.1, spec Part E.8).
 *
 * Shown only when the login's client list has >1 park (PortalPage gates it on
 * `data.parks.length > 1`). Chips: "All parks" first, then each park in
 * client-list order; the selected chip is filled. On phone (< md) the chip row
 * would overflow a narrow viewport, so it collapses to `shared/SelectMenu.js`.
 *
 * One truth (Part E.8 / Epic 9 goal): this control does NOT own park state. It
 * calls `onChange(parkId | null)` and the page persists `?park=` in the URL.
 * `null` is the all-parks operator view. The app-level property selector stays
 * the baseline; the page's dismissible "Showing {park} ✕" indicator (PortalPage)
 * clears the override and returns to that baseline.
 *
 * Controlled + presentational: every number/label comes from `parks` (the
 * `getPortal` `parks[]` — Part A.3), so it never imports the data layer.
 */
const ALL_PARKS = null;

const ParkFilter = ({ parks = [], value, onChange }) => {
  const list = Array.isArray(parks) ? parks : [];
  const options = [
    { value: ALL_PARKS, label: 'All parks' },
    ...list.map((p) => ({ value: p?.client_id ?? p?.id, label: p?.name || 'Park' })),
  ];

  return (
    <div data-testid="park-filter" className="flex flex-col gap-2">
      {/* Desktop / tablet: chips (Part E.8 — "All parks" first, then parks in
          client-list order; selected chip filled). */}
      <div
        className="hidden md:flex flex-wrap items-center gap-2"
        role="group"
        aria-label="Park filter"
      >
        {options.map((o) => {
          const active = o.value === value;
          return (
            <button
              key={`park-${String(o.value)}`}
              type="button"
              data-testid="park-chip"
              aria-pressed={active}
              onClick={() => onChange?.(o.value)}
              className={`inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] ${
                active
                  ? 'bg-[#32B1FF] text-white border-[#32B1FF]'
                  : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>

      {/* Phone: SelectMenu so the filter never overflows a narrow viewport. */}
      <div className="md:hidden">
        <SelectMenu
          ariaLabel="Park filter"
          value={value}
          onChange={onChange}
          options={options}
        />
      </div>
    </div>
  );
};

export default ParkFilter;
